import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import client, { makeGet, makePatch } from "@culturehq/client";
import { Button, Loader, Pagination, Table, Success, ActionButton, Icon } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination } from "app/hooks";
import { isMultiImageStory, isTarget, t, Trans } from "app/utils";

import { Time } from "components/common/Time";
import LightboxStories from "components/common/gallery/LightboxStories";
import PageHeader from "components/display/PageHeader";
import getUserName from "app/utils/getUserName";
import LightboxStoryQuickOptions from "components/common/gallery/LightboxStoryQuickOptions";
import useLatestMedia from "app/hooks/useLatestMedia";
import { deleteEntity, updateEntity } from "../mutations";
import StoryPromptTitle from "./StoryPromptTitle";
import StoryFilterBar from "../StoryFilterBar";
import { filtersToQuery, queryToOptions } from "../EmbedStoriesSlider";

const updateStory = updateEntity("stories");
const deleteStory = deleteEntity("stories");

const StoryRow = ({ story, onReviewClicked, onKeepAsInternalClicked, onDelete,
  onStoryUpdate
}) => {
  const { profile } = useProfile();

  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    lastMediaVersion
  } = useLatestMedia(story, profile, { landingPage: false });
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";

  return (
    <tr>
      <td className="StoryTable-Details StoryTable-clickable" onClick={() => onReviewClicked(story)}>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={story.media.thumbnail || story.media.url} alt={story.media.alt || story.question.question} />
          {story.media.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
          {isMultiImageStory(story) && <Icon className="StoryTable-Details__ImagesIcon" icon="images" />}
        </div>
      </td>
      <StoryPromptTitle onReviewClicked={onReviewClicked} story={story} />
      <td className="StoryTable-clickable" onClick={() => onReviewClicked(story)}>
        <h4 className="User">{getUserName(story.creator)}</h4>
        {!isTarget(profile.organization.name) && story.creator.companies?.map(company => (
          <div className="icon-building" key={company.id}>
            <Icon icon="building" />
            {company.name}
          </div>
        ))}
        <p><Time time={story.createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      <td className="Actions-column Actions-column__Pending-Review">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onStoryUpdate}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
        />
        <div className="pending-review-buttons">
          <Button icon="eye" onClick={() => onReviewClicked(story)}>
            <Trans>Review</Trans>
          </Button>
          <ActionButton icon="folder" onClick={() => onKeepAsInternalClicked(story)}>
            {profile.organization.storiesNeedApprovalEnabled ? (
              <Trans>Approve for Internal</Trans>
            ) : (<Trans>Keep as Internal</Trans>)}

          </ActionButton>
        </div>
      </td>
    </tr>
  );
};

const PendingReview = ({ organization }) => {
  if (organization.name === "RTX") {
    return (
      <>
        Please ensure the uploaded content adheres to the following guidelines:
        <ul>
          <li>
            <Trans>
              Confirm that no proprietary, sensitive, or Tech data is included. Videos should not
              be filmed on a production floor or in any workspace where RTX data is visible.
              Ensure backgrounds are neutral, such as a blank wall, exterior location, or company
              logo. Verify that computer screens are either off or only displaying our careers
              website (careers.rtx.com), and that any whiteboards or similar tools have been erased.
            </Trans>
          </li>
          <li>
            <Trans>
              Check that the topics discussed are broadly appealing and that no specific details
              about products, customers, processes, or methods used in daily work on RTX products
              are provided.
            </Trans>
          </li>
          <li>
            <Trans>
              Ensure that the filming location is quiet with good lighting and minimal background
              noise. If filmed in an office location, verify that no sensitive RTX information is
              being shared by others in the background.
            </Trans>
          </li>
        </ul>
      </>
    );
  }

  return (
    <Trans>
      Review the stories below and mark them as approved to be shared on social
      and/or your landing page. If they are to stay internal only, then select
      keep as internal. You can always change your choices after.
    </Trans>
  );
};

const StoriesPendingReview = ({ match }) => {
  const { profile, fetchedProfile } = useProfile();
  const history = useHistory();
  const [page, onPageClick] = usePagination();
  const [activeStory, setActiveStory] = useState();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const { fetching, json: { stories = [], pagination = {} }, onUpdate: onStoriesUpdate } = useFetch(
    () => {
      const query = filtersToQuery(selectedFilters);
      const params = { ...queryToOptions(query), states: ["pending"], page: page || 1 };
      const [bodySearch] = selectedFilters;
      if (typeof bodySearch === "string") params.bodySearch = bodySearch;
      return makeGet("/stories", params);
    },
    [page, selectedFilters]
  );

  const onUpdate = story => onStoriesUpdate(updateStory(story));
  const onDelete = story => onStoriesUpdate(deleteStory(story));

  useEffect(
    () => {
      const getStory = async id => {
        try {
          const { story } = await client.getStory({ storyId: id });
          if (story.externalState === "pending") {
            setActiveStory(story);
            setOpenModal(true);
          } else {
            history.push("/admin/story-reviews/pending");
          }
        } catch (error) {
          history.push("/admin/story-reviews/pending");
        }
      };

      if (match.params.storyId) {
        getStory(match.params.storyId);
      }
    },
    [match.params.storyId, history]
  );

  const handleReviewClicked = story => {
    setActiveStory(story);
    setOpenModal(true);
  };

  const handleClose = () => {
    if (match.params.storyId) history.push("/admin/story-reviews/pending");
    setOpenModal(false);
  };

  const handleStoryChange = goToIndex => {
    let target = goToIndex;

    if (stories.length === 1 && goToIndex === 1) {
      setOpenModal(false);
      setActiveStory(undefined);
      return;
    }

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    setActiveStory(stories[target]);
    setOpenModal(true);
  };

  const onStoryUpdate = updatedStory => {
    onUpdate(updatedStory);
    setActiveStory(updatedStory);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    const storyToUpdate = stories.find(oldStory => oldStory.id === story.id);
    onUpdate({ ...storyToUpdate, commentCount: numberOfComments });
  };

  const handleStoryRemove = storyId => {
    const storyToRemove = stories.find(oldStory => oldStory.id === storyId);
    if (storyToRemove) {
      onDelete(storyToRemove);
    }
    setOpenModal(false);
    setActiveStory(undefined);
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    const storyToUpdate = stories.find(oldStory => oldStory.id === storyId);
    onUpdate({ ...storyToUpdate, cheerForUser, cheersCount });
  };

  const onKeepAsInternalClicked = async ({ id }) => {
    const { story } = await makePatch(`/stories/${id}`, { externalState: "internal" });
    fetchedProfile({
      ...profile,
      pendingStoryReviews: profile.pendingStoryReviews
        .filter(pendingStoryId => pendingStoryId !== story.id)
    });
    onDelete(story);
  };

  const onApproveClicked = async ({ id }) => {
    const { story } = await makePatch(`/stories/${id}`, { externalState: "landing_page" });
    fetchedProfile({
      ...profile,
      pendingStoryReviews: profile.pendingStoryReviews
        .filter(pendingStoryId => pendingStoryId !== story.id)
    });
    onDelete(story);
  };

  const handleSelectedTags = (updatedOptions, _) => {
    setSelectedFilters(updatedOptions);
  };

  return (
    <div className="padding-left-15px padding-right-15px StoryApprovals">
      <LightboxStories
        className="gallery-lightbox--Story-Review"
        modalIsOpen={openModal}
        modalStyle={{ overlay: { overflow: "hidden" } }}
        activeStory={activeStory}
        currentUserAnswered
        stories={stories}
        onClose={handleClose}
        onCommentsChange={handleCommentsChange}
        onDelete={handleStoryRemove}
        onStoryCheerToggle={handleStoryCheerToggle}
        onStoryUpdate={onStoryUpdate}
        onStoryChange={handleStoryChange}
        reviewVersion
        onKeepAsInternalClicked={onKeepAsInternalClicked}
        onApproveClicked={onApproveClicked}
      />
      <PageHeader title={t("Stories Pending Review")}>
        <PendingReview organization={profile.organization} />
      </PageHeader>
      <StoryFilterBar
        creatable
        selectedOptions={selectedFilters}
        handleSelectedTags={handleSelectedTags}
        icon="ios-search-strong"
      />
      <Loader loading={fetching}>
        <Table className="StoryTable">
          <thead>
            <tr>
              <th className="thumb">
                <Trans>Story</Trans>
              </th>
              <th />
              <th className="users">
                <Trans>User</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {stories.map(story => (
              <StoryRow
                key={story.id}
                story={story}
                onReviewClicked={handleReviewClicked}
                onKeepAsInternalClicked={onKeepAsInternalClicked}
                onDelete={handleStoryRemove}
                onStoryUpdate={onStoryUpdate}
              />
            ))}
          </tbody>
        </Table>
        {stories.length < 1 && (
          <Success>
            <Trans>
              No more pending stories to review.
            </Trans>
          </Success>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

export default StoriesPendingReview;
